/**
 * What this build is pointed at.
 *
 * Read from the committed deployment manifest at build time rather than typed into the UI. An
 * address in a component is an address that outlives the deployment it belonged to, and a receipt
 * explorer showing the wrong contract is worse than one showing nothing.
 */

import manifest from "../../../../contracts/deployments/5042002.json";

export const CHAIN_ID = 5042002;
export const CHAIN_NAME = "Arc Public Testnet";
export const EXPLORER = "https://testnet.arcscan.app";

/**
 * The manifest carries metadata alongside addresses (chain id, compiler settings), so the address
 * lookup filters to string values rather than asserting the whole object is a string map. A cast
 * would have compiled and then rendered `viaIR: true` as a contract address.
 */
export const ADDRESSES: Record<string, string> = Object.fromEntries(
  Object.entries(manifest as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === "string" && entry[1].startsWith("0x"),
  ),
);

export const addressUrl = (address: string): string => `${EXPLORER}/address/${address}`;
export const txUrl = (hash: string): string => `${EXPLORER}/tx/${hash}`;

/** Shortens a hash for display without hiding which one it is. */
export const short = (value: string, lead = 10, tail = 8): string =>
  value.length <= lead + tail + 1 ? value : `${value.slice(0, lead)}…${value.slice(-tail)}`;

/**
 * Base units to a decimal string, without floating point.
 *
 * `Number(180000) / 1e6` is exact for these magnitudes and wrong for others, and a UI that is right
 * until the numbers get large is a UI nobody should reconcile against.
 */
export function formatUnits(value: bigint, decimals: number, symbol?: string): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(decimals + 1, "0");
  const whole = digits.slice(0, digits.length - decimals);
  const fraction = digits.slice(digits.length - decimals).replace(/0+$/, "");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const rendered = `${negative ? "-" : ""}${grouped}${fraction ? `.${fraction}` : ""}`;
  return symbol ? `${rendered} ${symbol}` : rendered;
}
